import React from 'react';
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';

interface StepperProgressProps {
  steps: string[];
  activeStep: number;
  completedSteps?: number[];
  incompleteSteps?: number[]; // kroky s chybějícími povinnými poli
  color?: 'primary' | 'success' | 'warning' | 'error';
  onStepClick?: (stepIndex: number) => void;
}

const StepperProgress: React.FC<StepperProgressProps> = ({ 
  steps, 
  activeStep, 
  completedSteps = [],
  incompleteSteps = [],
  color = 'primary',
  onStepClick
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getStepIcon = (stepIndex: number) => {
    const isClickable = onStepClick && (completedSteps.includes(stepIndex) || stepIndex === activeStep);
    
    const iconElement = () => {
      if (completedSteps.includes(stepIndex)) {
        return <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />;
      }
      if (stepIndex === activeStep) {
        return (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600,
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)',
                },
                '70%': {
                  boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)',
                },
              },
            }}
          >
            {stepIndex + 1}
          </Box>
        );
      }
      if (incompleteSteps.includes(stepIndex)) {
        return (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {stepIndex + 1}
          </Box>
        );
      }
      return <RadioButtonUnchecked sx={{ color: 'grey.400', fontSize: 28 }} />;
    };

    if (isClickable) {
      return (
        <Box
          onClick={() => onStepClick(stepIndex)}
          sx={{
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.2)',
            },
          }}
        >
          {iconElement()}
        </Box>
      );
    }
    
    return iconElement();
  };

  if (isMobile) {
    // Mobile compact view
    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Krok {activeStep + 1} z {steps.length}
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 2 
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              textAlign: 'center',
              color: `${color}.main`
            }}
          >
            {steps[activeStep]}
          </Typography>
        </Box>

        {/* Progress bar */}
        <Box sx={{ 
          width: '100%', 
          height: 4, 
          bgcolor: 'grey.200', 
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            width: `${((activeStep + 1) / steps.length) * 100}%`,
            height: '100%',
            bgcolor: `${color}.main`,
            borderRadius: 2,
            transition: 'width 0.3s ease'
          }} />
        </Box>
      </Box>
    );
  }

  // Desktop full view
  return (
    <Box sx={{ mb: 6 }}>
      <Stepper 
        activeStep={activeStep} 
        alternativeLabel
        sx={{
          '& .MuiStepConnector-root': {
            top: 12,
            '& .MuiStepConnector-line': {
              height: 2,
              border: 0,
              bgcolor: 'grey.200',
              borderRadius: 1,
            },
          },
          '& .MuiStepConnector-active .MuiStepConnector-line': {
            bgcolor: 'error.main',
          },
          '& .MuiStepConnector-completed .MuiStepConnector-line': {
            bgcolor: 'success.main',
          },
        }}
      >
        {steps.map((label, index) => {
          const isCompleted = completedSteps.includes(index);
          const isActive = activeStep === index;
          const isIncomplete = incompleteSteps.includes(index);
          const isClickable = onStepClick && (isCompleted || isActive);
          
          let labelColor = 'text.secondary';
          if (isCompleted) labelColor = 'success.main';
          else if (isActive) labelColor = 'error.main';
          else if (isIncomplete) labelColor = 'warning.main';
          
          return (
            <Step key={label}>
              <StepLabel
                icon={getStepIcon(index)}
                sx={{
                  cursor: isClickable ? 'pointer' : 'default',
                  '& .MuiStepLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 400,
                    color: labelColor,
                    mt: 1,
                    cursor: isClickable ? 'pointer' : 'default',
                  },
                  '& .MuiStepLabel-labelContainer': {
                    cursor: isClickable ? 'pointer' : 'default',
                  },
                }}
                onClick={isClickable ? () => onStepClick(index) : undefined}
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default StepperProgress;